/*
NOTE: the component's 'undocumented' spec from the project that originated the
forms feature, was to upload and validate a single file. An improvement, would
be to provide support for file input "multiple" files, through the forms backend
UI as an option.
*/

import AtpJSUpload, {
  type UploadPropsType as AtpjsUploadPropsType,
  type UploadStateType as AtpjsUploadStateType,
} from '@atpoint/atpjs/components/upload'
import { fileListToArray } from '@atpoint/atpjs/helpers/file'
import i18n from '@atpoint/atpjs/helpers/i18n'
import { Box, type WithStyles, withStyles } from '@material-ui/core'
import { createRef } from 'react'
import { StyledButton } from 'swisscard/components/buttons'
import { Link, P } from 'swisscard/components/typography'
import { uploadStyles } from './upload_styles'

import type { ErrorMessageType } from '@atpoint/atpjs/validation/validationsLib'
import type { ChangeEvent } from 'react'

export type MetaType = {
  step?: string
  field?: string
}

type UploadPropsType = AtpjsUploadPropsType & {
  errorMessage?: ErrorMessageType
  id?: string
  label?: string
  downloadDocument: () => void
  uploadDocument: (file: File, _meta: MetaType) => void
} & WithStyles<typeof uploadStyles>

class Upload extends AtpJSUpload<UploadPropsType, AtpjsUploadStateType> {
  fileInputRef = createRef<HTMLInputElement>()

  handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { maxFileSize, uploadDocument, meta } = this.props
    // @ts-ignore
    const eventFiles = event.target.files || event.dataTransfer.files
    if (eventFiles.length > 0) {
      const files = fileListToArray(eventFiles)
      const tooBig = files.find((file) => {
        return file.size > maxFileSize
      })
      const tooSmall = files.find((file) => {
        return file.size < 1
      })
      if (tooBig) {
        return alert(i18n.trans(['errors', 'fileTooBig'], { name: tooBig.name }))
      }
      if (tooSmall) {
        return alert(i18n.trans(['errors', 'fileTooSmall'], { name: tooSmall.name }))
      }
      uploadDocument(files[0], meta)
      return
    }
  }

  renderFileList() {
    const { documents, classes } = this.props

    if (documents.size === 0) {
      return null
    }

    return documents.map((document: Map<string, any>) => {
      if (document.get('error')) {
        return null
      }

      return (
        <Box key={document.get('id')} mb={3} className={classes.document}>
          <P noMargin>{document.get('filename')}</P>
          <P>
            <Link
              onClick={(event) => this.deleteDocument(event, document)}
              title={i18n.trans(['button', 'delete'])}
              destructive>
              {i18n.trans(['button', 'delete'])}
            </Link>
          </P>
        </Box>
      )
    })
  }

  render() {
    const { errorMessage, label, id, fileTypes } = this.props
    const showErrorMessage = typeof errorMessage === 'string'

    return (
      <>
        <P>{label}</P>
        {this.renderFileList()}
        <input
          accept={fileTypes}
          id={id}
          name={id}
          ref={this.fileInputRef}
          style={{ display: 'none' }}
          type="file"
          onChange={this.handleOnChange}
        />
        <StyledButton component="label" htmlFor={id} tabIndex={-1} hasTracking>
          {i18n.trans(['upload', 'selectFile'])}
        </StyledButton>
        {showErrorMessage && (
          <P size="small" variant="error">
            {errorMessage}
          </P>
        )}
      </>
    )
  }
}

export default withStyles(uploadStyles)(Upload)
